// eslint-disable-next-line jira/restricted/react
import React, { PureComponent } from 'react';
import type { Store } from 'redux';
import isEqual from 'lodash/isEqual';
import noop from 'lodash/noop';
import type { Locale } from '@jira/platform__supported-locales/src/supported-locales.tsx';
import type { ProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import { ReportErrors } from '@jira/platform__error-reporting/src/ReportErrors.tsx';
import ViewTracker from '@atlassian/jira-analytics-web-react/src/components/view-tracker.tsx';
import AppBase from '@atlassian/jira-app-base/src/index.tsx';
import AppStyle from '@atlassian/jira-common-components-app-style/src/index.tsx';
import { FlagsDispatcher } from '@atlassian/jira-flags';
import { FETCH_COMPONENTS_REASON } from './common/constants/fetch-components-reason.tsx';
import flagsMapper from './flags/index.tsx';
import rootEpic from './ops/index.tsx';
import { initialQuery } from './query-param-adapter/model/constants/initial-query.tsx';
import type { Query } from './query-param-adapter/model/index.tsx';
import { getComponentsPageRequest, setComponentsPage } from './state/actions.tsx';
import { set as setAnalyticsData } from './state/analytics/actions.tsx';
import { set as setContext } from './state/context/actions.tsx';
import createStore from './state/index.tsx';
import { set as setProject } from './state/project/actions.tsx';
import type { Project } from './state/project/types.tsx';
import { setQuery } from './state/query/actions.tsx';
import { getCurrentPage, getQuery } from './state/query/selectors.tsx';
import type { State } from './state/reducer.tsx';
import type { RequestResult } from './state/requests/retrieve.tsx';
import View from './view/view.tsx';

export type Props = {
	locale: Locale;
	projectKey: string;
	projectId: number;
	projectName: string;
	projectType: ProjectType;
	isAdmin: boolean;
	prefetchedData: RequestResult | null;
	onUnmount: () => void;
	query: Query;
	onQueryChanged: (query: Query) => void;
};

// eslint-disable-next-line jira/react/no-class-components
export default class ProjectComponentsApp extends PureComponent<Props> {
	static defaultProps = {
		isAdmin: false,
		prefetchedData: null,
		onUnmount: noop,
		query: initialQuery,
		onQueryChanged: noop,
	};

	constructor(props: Props) {
		super(props);

		const {
			locale,
			projectKey,
			projectId,
			projectName,
			isAdmin,
			projectType,
			prefetchedData,
			query,
			onQueryChanged,
		} = props;

		const project: Project = {
			id: projectId,
			key: projectKey,
			name: projectName,
			type: projectType,
		};

		const analyticsData = {
			projectType,
		};

		this.store = createStore(rootEpic);
		this.store.dispatch(setContext({ isAdmin, locale, onQueryChanged }));
		this.store.dispatch(setProject(project));
		this.store.dispatch(setAnalyticsData(analyticsData));
		this.store.dispatch(setQuery(query || initialQuery));

		if (prefetchedData) {
			this.store.dispatch(setComponentsPage(prefetchedData));
		} else {
			this.store.dispatch(
				getComponentsPageRequest({
					page: getCurrentPage(this.store.getState()),
					initiatedBy: FETCH_COMPONENTS_REASON.VIEW,
				}),
			);
		}
	}

	componentDidUpdate() {
		const { query } = this.props;
		if (query && query && !isEqual(query, getQuery(this.store.getState()))) {
			this.store.dispatch(setQuery(query));
		}
	}

	componentWillUnmount() {
		this.props.onUnmount();
	}

	store: Store<State>;

	render() {
		return (
			<AppBase store={this.store} hasErrorBoundary={false}>
				<ReportErrors
					id="unhandled"
					packageName="jiraProjectDirectoriesComponents"
					teamName="morphinecake"
					sendToPrivacyUnsafeSplunk
				>
					<AppStyle>
						{/* @ts-expect-error Type 'FlagsMapper<Action, State>' is not assignable to type 'FlagsMapper<AnyAction, State>'. */}
						<FlagsDispatcher mapper={flagsMapper} />
						<View />
						<ViewTracker />
					</AppStyle>
				</ReportErrors>
			</AppBase>
		);
	}
}
